import * as THREE from 'three';
import { Pulse } from '../animated';
import { FUTURE, translucent } from '../materials';
import type { ModelProps } from '../parts';

const { HULL, PANEL, DARK_PANEL, PAD } = FUTURE;

const PYLON_HEIGHT = 0.5;
const PYLON_BASE = 0.12;
const PYLON_TOP = 0.04;
const PYLON_BOTTOM = 0.09;
const BAND_RADIUS = 0.075;
const PYLON_TIP = PYLON_BASE + PYLON_HEIGHT;
const ORB_Y = PYLON_TIP + 0.06;
const DOME_RADIUS = 0.62;

const G = {
  pad: new THREE.CylinderGeometry(0.5, 0.52, 0.04, 6),
  plinth: new THREE.CylinderGeometry(0.16, 0.2, 0.08, 6),
  pylon: new THREE.CylinderGeometry(PYLON_TOP, PYLON_BOTTOM, PYLON_HEIGHT, 6),
  band: new THREE.CylinderGeometry(BAND_RADIUS, BAND_RADIUS, 0.03, 6),
  fin: new THREE.BoxGeometry(0.02, 0.26, 0.12),
  cradle: new THREE.TorusGeometry(0.075, 0.012, 5, 16).rotateX(Math.PI / 2),
  orb: new THREE.IcosahedronGeometry(0.06, 1),
  emitter: new THREE.BoxGeometry(0.05, 0.03, 0.05),
  dome: new THREE.SphereGeometry(DOME_RADIUS, 18, 8, 0, Math.PI * 2, 0, Math.PI / 2),
  rim: new THREE.TorusGeometry(DOME_RADIUS, 0.008, 4, 36).rotateX(Math.PI / 2),
};

// Three fins round the pylon's foot, and a small emitter on the pad under each.
const FIN_ANGLES = [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3];
const PULSE_SPEED = 2.4; // radians per second
const ORB_PULSE = 0.25;
const DOME_PULSE = 0.015;
const DOME_OPACITY = 0.12;

/** A band's width scale at height `y`, so it stands just proud of the tapering pylon. */
function bandScale(y: number): number {
  const t = (y - PYLON_BASE) / PYLON_HEIGHT;
  return (PYLON_BOTTOM - t * (PYLON_BOTTOM - PYLON_TOP) + 0.01) / BAND_RADIUS;
}

/**
 * A shield generator: a hex pad with a tapered white pylon braced by three
 * fins, team-coloured bands up its shaft, a glowing orb pulsing in a cradle on
 * top, and a faint team-tinted dome over the whole tile, breathing in time
 * with the orb.
 */
export function ShieldGenerator({ accent, glow }: ModelProps) {
  const field = translucent(glow, DOME_OPACITY);
  return (
    <>
      <mesh geometry={G.pad} material={PAD} position={[0, 0.02, 0]} />
      <mesh geometry={G.plinth} material={DARK_PANEL} position={[0, 0.08, 0]} />
      <mesh geometry={G.pylon} material={HULL} position={[0, PYLON_BASE + PYLON_HEIGHT / 2, 0]} />
      {[0.25, 0.42].map((y) => (
        <mesh
          key={y}
          geometry={G.band}
          material={accent}
          position={[0, y, 0]}
          scale={[bandScale(y), 1, bandScale(y)]}
        />
      ))}
      {FIN_ANGLES.map((a) => (
        <group key={a} rotation={[0, a, 0]}>
          <mesh geometry={G.fin} material={PANEL} position={[0, 0.25, 0.1]} />
          <mesh geometry={G.emitter} material={glow} position={[0, 0.055, 0.32]} />
        </group>
      ))}
      <mesh geometry={G.cradle} material={DARK_PANEL} position={[0, PYLON_TIP, 0]} />
      <Pulse amount={ORB_PULSE} speed={PULSE_SPEED} position={[0, ORB_Y, 0]}>
        <mesh geometry={G.orb} material={glow} />
      </Pulse>
      <Pulse amount={DOME_PULSE} speed={PULSE_SPEED}>
        <mesh geometry={G.dome} material={field} />
        <mesh geometry={G.rim} material={glow} position={[0, 0.01, 0]} />
      </Pulse>
    </>
  );
}
